import { type LoaderFunction, redirect, Route } from "react-router";

import { queryClient } from "@/providers/QueryClient";
import { orgStorage } from "@/shared/auth";
import {
  deviceSessionsQuery,
  prefetchDeviceSessions,
} from "@/shared/components/sessions";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { QueryTarget } from "@/shared/domain/query";
import { QueryService } from "@/shared/domain/query/QueryService";
import { makeViewsLoader } from "@/shared/domain/views/loaders/loadViews";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { DeviceTab, Goto } from "@/shared/navigation";
import { metricsWidgetRoutePatterns } from "@/shared/navigation/Goto";
import { composeLoaders } from "@/shared/routing/composeLoaders";
import {
  featureFlagRoute,
  makeFeatureFlagRedirectLoader,
} from "@/shared/routing/featureFlagRoute";
import { InheritOutletContext } from "@/shared/routing/InheritOutletContext";
import { RedirectToDefaultTab } from "@/shared/routing/RedirectToDefaultTab";

import { Device } from "./Device";
import { DeviceConfigurations } from "./DeviceConfigurations";
import { DeviceConfigurationsRedirect } from "./DeviceConfigurationsRedirect";
import { DeviceDatasets } from "./DeviceDatasets";
import { DeviceDetails } from "./DeviceDetails";
import { DeviceMetrics } from "./DeviceMetrics";
import { DeviceOverview } from "./DeviceOverview";
import { FILTERS_PARAM, readFilters } from "./DeviceOverview/overviewUrl";
import { Devices } from "./Devices";
import { DeviceSessions } from "./DeviceSessions";
import { DeviceTokens } from "./DeviceTokens";

/** The device page's path for one tab, from the route's `:deviceId`. */
const deviceTab = (deviceId: string | undefined, tab: DeviceTab) =>
  Goto.createDeviceRoute(deviceId ?? "", tab);

/**
 * Where opening a device lands: the Overview with `release-device-timeline`
 * on, else Datasets. The flag gate redirects to Datasets when the flag is off
 * and returns nothing when it is on, in which case this loader redirects to
 * Overview.
 */
const landOnDefaultTab: LoaderFunction = async (args) =>
  (await makeFeatureFlagRedirectLoader(queryClient, {
    flagName: "release-device-timeline",
    redirectTo: ({ params }) => deviceTab(params.deviceId, DeviceTab.Datasets),
  })(args)) ?? redirect(deviceTab(args.params.deviceId, DeviceTab.Overview));

/**
 * Starts the Overview's sessions query as the route matches, so the timeline
 * has its rows sooner. Not awaited: the page renders its skeleton meanwhile.
 * Only the unfiltered view: the filters' conditions depend on definitions
 * only the page's hooks have, and a query built without them would not be
 * the one the page reads.
 */
const prefetchOverviewSessions: LoaderFunction = ({ params, request }) => {
  const org = orgStorage.getCurrentOrg();
  const deviceId = params.deviceId;
  const filters = readFilters(
    new URL(request.url).searchParams.get(FILTERS_PARAM),
  );
  if (org && deviceId && filters.filters.length === 0) {
    void makeAuthenticatedHttpClient()
      .then((httpClient) =>
        prefetchDeviceSessions(
          queryClient,
          new QueryService(httpClient),
          deviceSessionsQuery({
            orgId: org.org_id,
            deviceId,
            filters,
            definitions: [],
          }),
        ),
      )
      // No client, no prefetch: the page makes its own requests.
      .catch(() => {});
  }
  return null;
};

export const DevicesRoutes = () => [
  /*
   * Opening the devices list must seed saved column order / page size
   * before <Devices /> mounts — `useResourceView` snapshots `preferences`
   * once via `React.useState` and never re-reads.
   */
  <Route
    key="devices-preferences-layout"
    loader={composeLoaders(
      loadSearchTablePreferences(
        queryClient,
        "device_search_table_preferences",
      ),
      // See `DatasetsRoutes`: resolves a `?view=` link before the list mounts.
      makeViewsLoader(queryClient, { target: QueryTarget.Devices }),
    )}
    element={<InheritOutletContext />}
  >
    <Route index element={<Devices />} />
  </Route>,
  <Route key={"id"} path=":deviceId" element={<Device />}>
    {/* Bare `/devices/:deviceId`, where every link to a device points, has no tab to render; redirect to the default. */}
    <Route index loader={landOnDefaultTab} />
    {featureFlagRoute({
      path: DeviceTab.Overview,
      flagName: "release-device-timeline",
      redirectTo: ({ params }) =>
        deviceTab(params.deviceId, DeviceTab.Datasets),
      element: <DeviceOverview />,
      loader: prefetchOverviewSessions,
    })}
    <Route
      path={DeviceTab.Datasets}
      loader={composeLoaders(
        loadSearchTablePreferences(
          queryClient,
          "dataset_search_table_preferences",
        ),
        // See `DatasetsRoutes`: resolves a `?view=` link before the list mounts.
        makeViewsLoader(queryClient, { target: QueryTarget.Datasets }),
      )}
      element={<DeviceDatasets />}
    />
    {/* `*` is the path of the folder open in the device's own section. */}
    {featureFlagRoute({
      path: `${DeviceTab.Configurations}/*`,
      flagName: "release-asset-store",
      redirectTo: ({ params }) =>
        deviceTab(params.deviceId, DeviceTab.Datasets),
      element: <DeviceConfigurations />,
    })}
    {/*
        Opening the Sessions tab must seed saved column order / page size
        before <DeviceSessions /> mounts — `useResourceView` snapshots
        `preferences` once via `React.useState` and never re-reads.
        `featureFlagRoute` already uses its loader slot for the flag
        gate, so this pathless wrapper Route runs the preferences loader.
    */}
    <Route
      key="sessions-preferences-layout"
      loader={loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      )}
      element={<InheritOutletContext />}
    >
      {featureFlagRoute({
        path: DeviceTab.Sessions,
        flagName: "release-sessions",
        redirectTo: ({ params }) =>
          deviceTab(params.deviceId, DeviceTab.Datasets),
        element: <DeviceSessions />,
      })}
    </Route>
    {/* The optional `:dashboardId?` segment carries the active saved
        dashboard (mirrors the top-level /metrics/:dashboardId? route);
        bare .../metrics is the "no dashboard selected" view. */}
    {featureFlagRoute({
      path: `${DeviceTab.Metrics}/:dashboardId?`,
      flagName: "release-metrics",
      redirectTo: ({ params }) =>
        deviceTab(params.deviceId, DeviceTab.Datasets),
      element: <DeviceMetrics />,
    })}
    {/* The shared widget-editor patterns are absolute (MetricsDashboard
        matches against them), so this parent's prefix is stripped. */}
    {Object.values(metricsWidgetRoutePatterns({ deviceScoped: true })).map(
      (pattern) =>
        featureFlagRoute({
          path: pattern.replace("/devices/:deviceId/", ""),
          flagName: "release-metrics",
          redirectTo: ({ params }) =>
            deviceTab(params.deviceId, DeviceTab.Datasets),
          element: <DeviceMetrics />,
        }),
    )}
    <Route path={DeviceTab.Details} element={<DeviceDetails />} />
    <Route path={DeviceTab.Tokens} element={<DeviceTokens />} />
    {/* Unknown subpath (typo, removed tab, stale bookmark): back to the bare device URL, which picks the default tab. */}
    <Route
      path="*"
      element={
        <RedirectToDefaultTab
          buildPath={({ deviceId }) => Goto.createDeviceRoute(deviceId ?? "")}
        />
      }
    />
  </Route>,
];

/**
 * `/device-configurations/:universalDeviceId/*`, where links to a device's
 * files land (see `Goto.route.DeviceConfigurationsByUniversalId`). It is a
 * top-level route because under `/devices` the segment after the prefix is
 * always the device ID the customer chose, never the `dv_` ID.
 */
export const DeviceConfigurationsRedirectRoute = () =>
  featureFlagRoute({
    path: `${Goto.route.DeviceConfigurationsByUniversalId}/:universalDeviceId/*`,
    flagName: "release-asset-store",
    redirectTo: Goto.route.Devices,
    element: <DeviceConfigurationsRedirect />,
  });
